/**
 * Company Sync Service
 * Handles fetching companies (projects), users, and assigning users to companies
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'
const ADMIN_API = 'https://api.aps.usa.autodesk.com/construction/admin/v1'
const HQ_API = 'https://developer.api.autodesk.com/hq/v1'

/**
 * Get access token from localStorage
 * @returns {string|null} Access token
 */
const getAccessToken = () => {
  return localStorage.getItem('aps_access_token')
}

/**
 * Get all companies (projects) the user has access to
 * @returns {Promise<Array>} Array of companies with {id, name, icon}
 */
export const getAllCompanies = async () => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/user/projects`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || `Failed to fetch companies (${response.status})`)
    }

    const data = await response.json()
    const projects = data.projects || []

    // Transform projects to company format
    return projects.map(project => ({
      id: project.id,
      name: project.name,
      icon: '🏢' // Default icon, can be customized based on project type
    }))
  } catch (error) {
    console.error('[companySyncService] Error fetching companies:', error)
    throw error
  }
}

/**
 * Get account ID from project (needed for HQ API)
 * @param {string} projectId - Project ID
 * @returns {Promise<string>} Account ID
 */
export const getAccountIdFromProject = async (projectId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    // First try to get account ID from project details via backend
    const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.ok) {
      const data = await response.json()
      const accountId = data.project?.account_id || data.account_id
      if (accountId) {
        console.log('[companySyncService] Account ID found:', accountId)
        return accountId
      }
    }

    // If backend doesn't have it, try ACC Admin API
    const adminResponse = await fetch(`${ADMIN_API}/projects/${projectId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (adminResponse.ok) {
      const adminData = await adminResponse.json()
      const accountId = adminData.account_id
      if (accountId) {
        console.log('[companySyncService] Account ID from ACC API:', accountId)
        return accountId
      }
    }

    throw new Error('Could not determine account ID from project')
  } catch (error) {
    console.error('[companySyncService] Error getting account ID:', error)
    throw error
  }
}

/**
 * Get all companies from HQ API
 * @param {string} accountId - Account ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of companies to fetch (default: 200)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @returns {Promise<Array>} Array of companies with detailed information
 */
export const getAccountCompanies = async (accountId, options = {}) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    // Build query parameters
    const params = new URLSearchParams({
      limit: options.limit || 200,
      offset: options.offset || 0
    })

    // Use HQ API to get companies
    const url = `${HQ_API}/accounts/${accountId}/companies?${params.toString()}`

    console.log('[companySyncService] Fetching companies from HQ API:', url)

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[companySyncService] HQ API Error:', errorData)
      throw new Error(`Failed to fetch companies from HQ API (${response.status}): ${errorData.detail || errorData.title || 'Unknown error'}`)
    }

    const data = await response.json()

    console.log('[companySyncService] HQ API companies fetched:', {
      accountId,
      count: data.results?.length || data.length || 0,
      pagination: data.pagination
    })

    // Handle response format
    const companiesArray = data.results || (Array.isArray(data) ? data : [])

    // Transform company data to our format
    const companies = companiesArray.map(company => ({
      id: company.id,
      name: company.name,
      icon: '🏢',
      trade: company.trade || null,
      address_line_1: company.address_line_1 || null,
      address_line_2: company.address_line_2 || null,
      city: company.city || null,
      state_or_province: company.state_or_province || null,
      postal_code: company.postal_code || null,
      country: company.country || null,
      phone: company.phone || null,
      website_url: company.website_url || null,
      description: company.description || null,
      company_type: company.company_type || null,
      account_id: accountId,
      _isHQCompany: true
    }))

    return companies
  } catch (error) {
    console.error('[companySyncService] Error fetching HQ companies:', error)
    throw error
  }
}

/**
 * Get all companies (contractors/organizations) in a specific project
 * Uses HQ API to fetch companies from the project's account
 * @param {string} projectId - Project ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of companies to fetch (default: 200)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @returns {Promise<Array>} Array of companies with detailed information
 */
export const getProjectCompanies = async (projectId, options = {}) => {
  try {
    // First, get the account ID from the project
    const accountId = await getAccountIdFromProject(projectId)

    // Then fetch companies from HQ API using the account ID
    const companies = await getAccountCompanies(accountId, {
      limit: options.limit || 200,
      offset: options.offset || 0
    })

    return companies
  } catch (error) {
    console.error('[companySyncService] Error in getProjectCompanies:', error)
    throw error
  }
}

/**
 * Get all users across all accessible projects
 * @returns {Promise<Array>} Array of users with {id, name, email, company, avatar, role_id, company_id}
 */
export const getAllUsers = async () => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    // First, get all projects
    const companiesResponse = await fetch(`${API_BASE_URL}/api/auth/user/projects`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })

    if (!companiesResponse.ok) {
      throw new Error('Failed to fetch projects')
    }

    const companiesData = await companiesResponse.json()
    const projects = companiesData.projects || []

    // Fetch users from all projects in parallel
    const allUsersPromises = projects.map(async (project) => {
      try {
        const response = await fetch(
          `${ADMIN_API}/projects/${project.id}/users`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) {
          console.warn(`Failed to fetch users for project ${project.name}`)
          return []
        }

        const data = await response.json()
        const users = data.results || []

        // Transform users to include project/company information
        return users.map(user => ({
          id: user.id || user.autodeskId || user.oxygenId,
          name: user.name,
          email: user.email,
          company: user.company_name || project.name,
          company_id: user.company_id,
          role_id: user.role_id,
          role_name: user.role_name,
          status: user.status,
          avatar: null, // ACC API doesn't provide avatars
          _projectId: project.id,
          _projectName: project.name
        }))
      } catch (error) {
        console.warn(`Error fetching users for project ${project.name}:`, error)
        return []
      }
    })

    const allUsersArrays = await Promise.all(allUsersPromises)
    const allUsers = allUsersArrays.flat()

    // Remove duplicates (users might be in multiple projects)
    const uniqueUsers = Array.from(
      new Map(allUsers.map(user => [user.email, user])).values()
    )

    console.log('[companySyncService] Fetched users:', {
      totalProjects: projects.length,
      totalUsers: allUsers.length,
      uniqueUsers: uniqueUsers.length
    })

    return uniqueUsers
  } catch (error) {
    console.error('[companySyncService] Error fetching users:', error)
    throw error
  }
}

/**
 * Get users for a specific project/company
 * @param {string} projectId - Project/Company ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of users to fetch (default: 100)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {string} options.sort - Sort order (default: 'name asc')
 * @param {string} options.orFilters - OR filters (default: 'email,name')
 * @param {string} options.filterName - Filter by name
 * @param {string} options.filterEmail - Filter by email
 * @returns {Promise<Object>} Object with users array and pagination info
 */
export const getCompanyUsers = async (projectId, options = {}) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    // Build query parameters
    const params = new URLSearchParams({
      limit: options.limit || 100,
      offset: options.offset || 0,
      sort: options.sort || 'name asc',
      orFilters: options.orFilters || 'email,name'
    })

    // Add optional filters
    if (options.filterName !== undefined) {
      params.append('filter[name]', options.filterName)
    }
    if (options.filterEmail !== undefined) {
      params.append('filter[email]', options.filterEmail)
    }

    const url = `${ADMIN_API}/projects/${projectId}/users?${params.toString()}`

    console.log('[companySyncService] Fetching users from:', url)

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch users for project ${projectId} (${response.status})`)
    }

    const data = await response.json()
    const users = data.results || []

    console.log('[companySyncService] Fetched users:', {
      projectId,
      count: users.length,
      pagination: data.pagination
    })

    const transformedUsers = users.map(user => ({
      id: user.id || user.autodeskId || user.oxygenId,
      name: user.name,
      email: user.email,
      company: user.company_name,
      company_id: user.company_id,
      role_id: user.role_id,
      role_name: user.role_name,
      status: user.status,
      avatar: null,
      _projectId: projectId
    }))

    return {
      users: transformedUsers,
      pagination: data.pagination
    }
  } catch (error) {
    console.error('[companySyncService] Error fetching company users:', error)
    throw error
  }
}

/**
 * Assign a user to a company
 * Updates the user's company assignment in a project using PATCH
 *
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID to assign
 * @param {string} companyId - Company ID to assign the user to
 * @returns {Promise<object>} Assignment result
 */
export const assignUserToCompany = async (projectId, userId, companyId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    // Use ACC Admin API to update user's company assignment
    // PATCH /projects/{projectId}/users/{userId}
    const url = `${ADMIN_API}/projects/${projectId}/users/${userId}`

    console.log('[companySyncService] Assigning user to company:', {
      url,
      projectId,
      userId,
      companyId
    })

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        companyId: companyId
      })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('[companySyncService] Assignment error:', errorData)
      throw new Error(errorData.message || errorData.detail || `Failed to assign user to company (${response.status})`)
    }

    const data = await response.json()

    console.log('[companySyncService] User assigned successfully:', {
      userId,
      companyId,
      response: data
    })

    return {
      success: true,
      data
    }
  } catch (error) {
    console.error('[companySyncService] Error assigning user:', error)
    throw error
  }
}

/**
 * Update user's role in a company (project)
 *
 * @param {string} userId - User ID
 * @param {string} companyId - Company (Project) ID
 * @param {string} roleId - New role ID
 * @returns {Promise<object>} Update result
 */
export const updateUserRole = async (userId, companyId, roleId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    const response = await fetch(
      `${ADMIN_API}/projects/${companyId}/users/${userId}`,
      {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role_id: roleId
        })
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update user role (${response.status})`)
    }

    const data = await response.json()

    console.log('[companySyncService] User role updated:', {
      userId,
      companyId,
      roleId
    })

    return {
      success: true,
      data
    }
  } catch (error) {
    console.error('[companySyncService] Error updating user role:', error)
    throw error
  }
}

/**
 * Remove user from a company (project)
 *
 * @param {string} userId - User ID
 * @param {string} companyId - Company (Project) ID
 * @returns {Promise<object>} Removal result
 */
export const removeUserFromCompany = async (userId, companyId) => {
  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  try {
    const response = await fetch(
      `${ADMIN_API}/projects/${companyId}/users/${userId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to remove user (${response.status})`)
    }

    console.log('[companySyncService] User removed from company:', {
      userId,
      companyId
    })

    return {
      success: true
    }
  } catch (error) {
    console.error('[companySyncService] Error removing user:', error)
    throw error
  }
}

export default {
  getAllCompanies,
  getProjectCompanies,
  getAccountCompanies,
  getAccountIdFromProject,
  getAllUsers,
  getCompanyUsers,
  assignUserToCompany,
  updateUserRole,
  removeUserFromCompany
}
